import type { ComponentPropsWithoutRef } from 'react'
import { ploteaApp } from '../lib/productProfile'

export function ArchigmaMark({
  className,
  ...props
}: ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      viewBox="121 58 227 326"
      role="img"
      aria-label="Archigma"
      className={`archigma-lockup ${className ?? ''}`}
      {...props}
    >
      <path d="M348 58 249 116v182l42 86h57Z" fill="currentColor" />
      <path d="m220 162-99 57v165h65l34-86Z" fill="currentColor" />
    </svg>
  )
}

export function ArchigmaLogo({ className }: { className?: string }) {
  return (
    <img
      src="/archigma-logo-horizontal.svg"
      alt="Archigma"
      className={`archigma-lockup ${className ?? ''}`}
    />
  )
}

/**
 * Plotea stacked lockup (canonical `assets/brand/plotea-logo-stacked.svg`,
 * served at `/brand/` by the Plotea web build). Ink artwork; the shared
 * `archigma-lockup` rule inverts it in dark mode.
 */
export function PloteaLogo({ className }: { className?: string }) {
  return (
    <img
      src="/brand/plotea-logo-stacked.svg"
      alt="Plotea"
      className={`archigma-lockup ${className ?? ''}`}
    />
  )
}

/**
 * Inline Plotea Two Parcels symbol (geometry of `assets/brand/plotea-symbol.svg`)
 * in `currentColor`, so it follows the theme and needs no `/brand/` asset.
 */
export function PloteaSymbol({
  className,
  ...props
}: ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      viewBox="0 0 256 256"
      role="img"
      aria-label="Plotea"
      className={className}
      {...props}
    >
      <g fill="currentColor">
        <polygon points="64,0 224,0 224,64 96,160 64,224 0,224 0,64" />
        <polygon points="232.44582472,77.66563146 256,101.21980674 256,256 101.21980674,256 77.66563146,232.44582472 108.621670112,170.533747416" />
      </g>
    </svg>
  )
}

/**
 * The outlined Plotea wordmark, cut unchanged from the approved stacked lockup
 * (`assets/brand/plotea-logo-stacked.svg`, group `plotea-wordmark`), with
 * tight bounds, in `currentColor`.
 */
export function PloteaWordmark({
  className,
  ...props
}: ComponentPropsWithoutRef<'svg'>) {
  return (
    <svg
      viewBox="27 55 285.1 90"
      role="img"
      aria-label="Plotea"
      className={className}
      {...props}
    >
      <path
        fill="currentColor"
        d="m 27,145 h 12.6 v -25.7 h 1.8 c 2.1,3.5 6.4,7.1 15.4,7.1 12,0 22.4,-9.2 22.4,-25.4 V 99.4 C 79.2,83.2 68.7,74 56.8,74 c -9,0 -13.3,3.6 -15.6,7.4 h -1.8 v -6 H 27 Z m 26,-29.6 c -7.8,0 -13.6,-5.1 -13.6,-14.7 v -1 C 39.4,90.1 45.3,85 53,85 c 7.7,0 13.6,5.1 13.6,14.7 v 1 c 0,9.6 -5.8,14.7 -13.6,14.7 z M 87.800049,125 H 100.40005 V 55 H 87.800049 Z m 47.200071,1.4 c 14.8,0 26,-9.4 26,-25.4 v -1.6 c 0,-16 -11.2,-25.4 -26,-25.4 -14.8,0 -26,9.4 -26,25.4 v 1.6 c 0,16 11.2,25.4 26,25.4 z m 0,-11.2 c -7.7,0 -13.4,-5.2 -13.4,-14.5 v -1 c 0,-9.3 5.6,-14.5 13.4,-14.5 7.8,0 13.4,5.2 13.4,14.5 v 1 c 0,9.3 -5.7,14.5 -13.4,14.5 z M 187,125 h 14 v -10.4 h -9.6 c -1.8,0 -2.8,-1 -2.8,-3 V 85.8 h 13.6 V 75.4 H 188.6 V 60 H 176 V 75.4 H 163.6 V 85.8 H 176 v 28 c 0,6.8 4.4,11.2 11,11.2 z m 44.39997,1.4 c 13.6,0 19.5,-7.7 22.3,-13 l -10.3,-5.4 c -1.7,3.8 -4.6,7.4 -11.6,7.4 -7,0 -12.3,-4.7 -12.6,-11.5 h 36.1 v -4.3 c 0,-15.2 -9.5,-25.6 -24.1,-25.6 -14.8,0 -24.8,10 -24.8,25.6 v 1.2 c 0,15.6 10.1,25.6 25,25.6 z m -12.1,-31.9 c 0.9,-5.7 5,-9.5 11.8,-9.5 6.5,0 10.8,3.8 11.4,9.5 z m 59.59997,31.9 c 10.4,0 13.5,-5.6 14.4,-8.1 h 1.9 v 0.1 c 0,4 3.6,6.6 8.5,6.6 h 8.4 v -10.4 h -4 c -1.8,0 -2.8,-1 -2.8,-3 V 93.1 c 0,-12.4 -8.2,-19.1 -21.2,-19.1 -12.9,0 -19.4,6.5 -21.8,14 l 11.6,3.9 c 0.9,-4.5 3.7,-7.7 10,-7.7 6.4,0 9.2,3.4 9.2,8 V 95 h -13.6 c -11.2,0 -19.2,5.3 -19.2,15.5 0,10.2 8,15.9 18.6,15.9 z m 2.2,-10.2 c -5.4,0 -8.2,-2.5 -8.2,-6.1 0,-3.6 2.8,-5.7 7.5,-5.7 h 12.7 v 1 c 0,6.6 -5,10.8 -12,10.8 z"
      />
    </svg>
  )
}

/** Plotea Two Parcels symbol (`assets/brand/plotea-symbol.svg`). */
export function PloteaMark({ className }: { className?: string }) {
  return (
    <img
      src="/brand/plotea-symbol.svg"
      alt="Plotea"
      className={`archigma-lockup ${className ?? ''}`}
    />
  )
}

/**
 * The product's primary logo: Archigma's horizontal wordmark, or Plotea's
 * stacked lockup in the Plotea web app (`ploteaClassName` sizes it, since the
 * stacked lockup is near-square).
 */
export function ProductLogo({
  className,
  ploteaClassName,
}: {
  className?: string
  ploteaClassName?: string
}) {
  return ploteaApp ? (
    <PloteaLogo className={ploteaClassName ?? className} />
  ) : (
    <ArchigmaLogo className={className} />
  )
}

export function BrandHeader({
  topic,
  compact = false,
  metadata = 'Brand system · 2026',
}: {
  topic: string
  compact?: boolean
  metadata?: string | null
}) {
  return (
    <header
      className={`grid grid-cols-[minmax(0,1fr)_auto] items-start border-b border-border ${compact ? 'gap-4 pb-4' : 'gap-8 pb-6'}`}
    >
      <div className="grid justify-items-start gap-2">
        <ProductLogo
          className={compact ? 'h-auto w-44' : 'h-auto w-64'}
          ploteaClassName={compact ? 'h-auto w-24' : 'h-auto w-32'}
        />
        <div className="font-mono text-[0.68rem] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {topic}
        </div>
      </div>
      {metadata !== null && (
        <div className="pt-3 text-right font-mono text-[0.68rem] font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {metadata}
        </div>
      )}
    </header>
  )
}
